/**
 * Discussions asynchrones tuteur ↔ élève (migrations 0265-0267). Tables
 * postérieures aux types générés (stale volontaire, cf. CLAUDE.md) → client
 * générique, calque `apps/app/src/lib/tutor/rendezvous.ts`. Un seul
 * consommateur (`apps/app`) : pas de package workspace dédié.
 */

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { createDocumentsClient } from '@jeraisonne/documents';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/auth';
import { sendNotification } from '@/lib/notifications/send-notification';
import { mergeDiscussionTimeline } from './discussion-timeline';
import { buildDiscussionSubject, type DiscussionSubjectInput } from './discussion-subject';
import {
  DiscussionError,
  discussionFromRow,
  discussionWriteError,
  type DiscussionRow,
  type DiscussionTimelineMessage,
  type RawDiscussionMessageRow,
  type RawDiscussionRow,
  type RawRoomMessageRow,
} from './types';

const db = supabase as unknown as SupabaseClient;
const documentsClient = createDocumentsClient(db);

// ⚠️ 2 FK vers profiles (tutor_id, student_id) → désambiguïser (cf. CLAUDE.md).
const DISCUSSION_SELECT =
  'id, tutor_id, student_id, title, subject_scope, exercise_id, submission_id, chapter_id, created_by, created_at, last_message_at, last_read_at_tutor, last_read_at_student, ' +
  'tutor:profiles!discussions_tutor_id_fkey(display_name:visible_name, avatar_url), ' +
  'student:profiles!discussions_student_id_fkey(display_name:visible_name, avatar_url)';

function discussionsKey(userId: string | undefined) {
  return ['discussions', userId] as const;
}

/** Toutes les discussions de l'utilisateur (tuteur ou élève), triées par activité récente. */
export function useDiscussions() {
  const { user } = useAuth();
  return useQuery({
    queryKey: discussionsKey(user?.id),
    enabled: !!user?.id,
    staleTime: 15_000,
    queryFn: async (): Promise<DiscussionRow[]> => {
      const { data, error } = await db
        .from('discussions')
        .select(DISCUSSION_SELECT)
        .or(`tutor_id.eq.${user?.id},student_id.eq.${user?.id}`)
        .order('last_message_at', { ascending: false });
      if (error) throw new DiscussionError('list_failed', 'Impossible de charger tes discussions.');
      return ((data ?? []) as unknown as RawDiscussionRow[]).map(discussionFromRow);
    },
  });
}

/** Détail d'une discussion (RLS = participant, sinon `null`). */
export function useDiscussion(discussionId: string | undefined) {
  return useQuery({
    queryKey: ['discussion', discussionId],
    enabled: !!discussionId,
    queryFn: async (): Promise<DiscussionRow | null> => {
      const { data, error } = await db
        .from('discussions')
        .select(DISCUSSION_SELECT)
        .eq('id', discussionId as string)
        .maybeSingle();
      if (error)
        throw new DiscussionError('list_failed', 'Impossible de charger cette discussion.');
      return data ? discussionFromRow(data as unknown as RawDiscussionRow) : null;
    },
  });
}

/**
 * Fil fusionné d'une discussion : ses messages asynchrones + ceux des salles
 * visio qui lui sont rattachées (calque `useRendezVousDialogue`, deux sources
 * en parallèle puis fusion — cf. `mergeDiscussionTimeline`).
 */
export const discussionMessagesKey = (discussionId: string | undefined) =>
  ['discussion-messages', discussionId] as const;

export function useDiscussionMessages(discussionId: string | undefined) {
  return useQuery({
    queryKey: discussionMessagesKey(discussionId),
    enabled: !!discussionId,
    // Filet de sécurité si le canal temps réel décroche (réseau mobile).
    refetchInterval: 30_000,
    queryFn: async (): Promise<DiscussionTimelineMessage[]> => {
      const [messagesRes, roomsRes] = await Promise.all([
        db
          .from('discussion_messages')
          .select('id, discussion_id, sender_id, body, source, document_id, hidden_at, created_at')
          .eq('discussion_id', discussionId as string)
          .order('created_at', { ascending: true }),
        db
          .from('video_rooms')
          .select('id')
          .eq('discussion_id', discussionId as string),
      ]);
      if (messagesRes.error) {
        throw new DiscussionError('list_failed', 'Impossible de charger les messages.');
      }
      if (roomsRes.error) {
        throw new DiscussionError('list_failed', 'Impossible de charger les messages.');
      }

      const roomIds = ((roomsRes.data ?? []) as Array<{ id: string }>).map((r) => r.id);
      let roomMessages: RawRoomMessageRow[] = [];
      if (roomIds.length > 0) {
        const { data, error } = await db
          .from('visio_room_messages')
          .select('id, room_id, sender_id, body, source, document_id, hidden_at, created_at')
          .in('room_id', roomIds)
          .order('created_at', { ascending: true });
        if (error) throw new DiscussionError('list_failed', 'Impossible de charger les messages.');
        roomMessages = (data ?? []) as unknown as RawRoomMessageRow[];
      }

      return mergeDiscussionTimeline(
        (messagesRes.data ?? []) as unknown as RawDiscussionMessageRow[],
        roomMessages,
      );
    },
  });
}

function invalidateDiscussion(qc: ReturnType<typeof useQueryClient>, discussionId: string) {
  void qc.invalidateQueries({ queryKey: ['discussion-messages', discussionId] });
  void qc.invalidateQueries({ queryKey: ['discussion', discussionId] });
  void qc.invalidateQueries({ queryKey: ['discussions'] });
}

/**
 * Temps réel d'un fil : tout message ajouté / masqué dans la discussion (ou
 * dans une salle visio qui lui est rattachée) recharge le fil chez les deux
 * participants. `postgres_changes` est filtré par la RLS de chaque table
 * (participant de la discussion / membre de la salle) ; les deux tables sont
 * dans la publication `supabase_realtime`.
 */
export function useDiscussionRealtime(
  discussionId: string | undefined,
  roomIds: readonly string[] = [],
) {
  const qc = useQueryClient();
  const roomKey = [...roomIds].sort().join(',');

  useEffect(() => {
    if (!discussionId) return;
    const refresh = () => {
      void qc.invalidateQueries({ queryKey: discussionMessagesKey(discussionId) });
      void qc.invalidateQueries({ queryKey: ['discussions'] });
    };
    const rooms = roomKey ? roomKey.split(',') : [];
    let channel = supabase
      .channel(`discussion-messages:${discussionId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'discussion_messages',
          filter: `discussion_id=eq.${discussionId}`,
        },
        refresh,
      )
      // UPDATE (masquage) : pas de filtre serveur fiable sur une colonne non-PK
      // (identité de réplica par défaut) → filtre côté client.
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'discussion_messages' },
        (payload) => {
          const row = payload.new as { discussion_id?: string };
          if (row.discussion_id === discussionId) refresh();
        },
      );
    for (const roomId of rooms) {
      channel = channel.on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'visio_room_messages',
          filter: `room_id=eq.${roomId}`,
        },
        refresh,
      );
    }
    channel.subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [discussionId, roomKey, qc]);
}

/** Message affiché tout de suite chez l'expéditeur, remplacé au rechargement. */
export function buildOptimisticMessage(
  body: string,
  senderId: string,
  now: Date = new Date(),
): DiscussionTimelineMessage {
  const tempId = `pending-${now.getTime()}-${Math.random().toString(36).slice(2, 8)}`;
  return {
    id: `disc:${tempId}`,
    rawId: tempId,
    origin: 'discussion',
    roomId: null,
    senderId,
    body,
    source: 'typed',
    documentId: null,
    hiddenAt: null,
    createdAt: now.toISOString(),
  };
}

export function useSendDiscussionMessage(discussionId: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    // Affichage immédiat (optimiste) : le message apparaît dès l'envoi, puis
    // la vraie ligne le remplace au rechargement du fil.
    onMutate: async (body: string) => {
      const key = discussionMessagesKey(discussionId);
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<DiscussionTimelineMessage[]>(key);
      if (user?.id) {
        qc.setQueryData<DiscussionTimelineMessage[]>(key, (old) => [
          ...(old ?? []),
          buildOptimisticMessage(body, user.id),
        ]);
      }
      return { previous };
    },
    onError: (_err, _body, context) => {
      if (context?.previous !== undefined) {
        qc.setQueryData(discussionMessagesKey(discussionId), context.previous);
      }
    },
    mutationFn: async (body: string) => {
      const { error } = await db.rpc('send_discussion_message', {
        p_discussion_id: discussionId,
        p_body: body,
      });
      if (error) throw discussionWriteError(error.message, "Le message n'a pas pu être envoyé.");
    },
    onSuccess: () => {
      invalidateDiscussion(qc, discussionId);
      void sendNotification({ type: 'discussion_message_sent', discussion_id: discussionId });
    },
  });
}

export function useSendDiscussionFile(discussionId: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (file: File) => {
      if (!user?.id) throw new DiscussionError('not_authorized', 'Session expirée.');
      const doc = await documentsClient.upload({
        file,
        target: { kind: 'discussion', discussionId },
        userId: user.id,
      });
      const { error } = await db.rpc('send_discussion_file', {
        p_discussion_id: discussionId,
        p_document_id: doc.id,
      });
      if (error) {
        // Le fichier est déposé (visible dans « Mes fichiers ») même si la
        // publication échoue — l'appelant peut réessayer sans redéposer.
        throw discussionWriteError(
          error.message,
          "Le fichier est enregistré, mais n'a pas pu être publié dans le fil.",
        );
      }
    },
    onSuccess: () => {
      invalidateDiscussion(qc, discussionId);
      void sendNotification({ type: 'discussion_message_sent', discussion_id: discussionId });
    },
  });
}

/**
 * Masque/rétablit un message du fil FUSIONNÉ — `origin` dit quelle RPC appeler :
 * un message venu d'une salle visio rattachée reste un `visio_room_messages`,
 * jamais touché par `set_discussion_message_hidden` (deux systèmes d'écriture
 * séparés, cf. le choix d'unification à l'affichage seulement).
 */
export function useSetMessageHidden(discussionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      origin: 'discussion' | 'room';
      messageId: string;
      hidden: boolean;
    }) => {
      const rpc =
        input.origin === 'discussion'
          ? 'set_discussion_message_hidden'
          : 'set_visio_room_message_hidden';
      const { error } = await db.rpc(rpc, {
        p_message_id: input.messageId,
        p_hidden: input.hidden,
      });
      if (error) throw discussionWriteError(error.message, 'Action impossible.');
    },
    onSuccess: () => invalidateDiscussion(qc, discussionId),
  });
}

/**
 * Titre déduit par l'IA du contenu de la discussion (edge fn
 * `discussion-generate-title`, étape `discussion_title_generate`, migration
 * 0268) — appelée en BEST-EFFORT, fire-and-forget, juste après l'envoi d'un
 * message dans une discussion `free` encore au placeholder
 * (`DISCUSSION_PLACEHOLDER_TITLE`). Aucune erreur n'est jamais levée : au pire
 * le placeholder reste, ce qui est un état parfaitement acceptable — jamais
 * affiché à l'utilisateur.
 */
export function useGenerateDiscussionTitle(discussionId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<string | null> => {
      const { data, error } = await db.functions.invoke('discussion-generate-title', {
        body: { discussion_id: discussionId },
      });
      if (error) return null;
      const title = (data as { ok?: boolean; title?: string } | null)?.title;
      return title ?? null;
    },
    onSuccess: (title) => {
      if (title) invalidateDiscussion(qc, discussionId);
    },
  });
}

export function useMarkDiscussionRead(discussionId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      if (!discussionId) return;
      const { error } = await db.rpc('mark_discussion_read', { p_discussion_id: discussionId });
      if (error) throw new DiscussionError('send_failed', 'Action impossible.');
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['discussions'] });
      if (discussionId) void qc.invalidateQueries({ queryKey: ['discussion', discussionId] });
    },
  });
}

export interface CreateDiscussionInput {
  tutorId: string;
  studentId: string;
  subject: DiscussionSubjectInput;
}

export function useCreateDiscussion() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateDiscussionInput): Promise<DiscussionRow> => {
      if (!user?.id) throw new DiscussionError('not_authorized', 'Session expirée.');
      const subject = buildDiscussionSubject(input.subject);
      const { data, error } = await db
        .from('discussions')
        .insert({
          tutor_id: input.tutorId,
          student_id: input.studentId,
          created_by: user.id,
          title: subject.title,
          subject_scope: subject.subjectScope,
          exercise_id: subject.exerciseId,
          submission_id: subject.submissionId,
          chapter_id: subject.chapterId,
        })
        .select(DISCUSSION_SELECT)
        .single();
      if (error || !data) {
        throw new DiscussionError(
          'create_failed',
          "La discussion n'a pas pu être créée : vérifie que ce contact est toujours lié.",
        );
      }
      return discussionFromRow(data as unknown as RawDiscussionRow);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['discussions'] }),
  });
}

/**
 * Retrouve la discussion déjà ouverte sur ce sujet (exercice/soumission), ou en
 * crée une — protégé par les index uniques partiels de 0265. Alimente les
 * entrées « Discuter de cet exercice / cette soumission ».
 */
export function useFindOrCreateDiscussion() {
  const create = useCreateDiscussion();
  return useMutation({
    mutationFn: async (input: CreateDiscussionInput): Promise<DiscussionRow> => {
      if (input.subject.scope === 'exercise' || input.subject.scope === 'submission') {
        const column = input.subject.scope === 'exercise' ? 'exercise_id' : 'submission_id';
        const targetId =
          input.subject.scope === 'exercise'
            ? input.subject.exerciseId
            : input.subject.submissionId;
        const { data, error } = await db
          .from('discussions')
          .select(DISCUSSION_SELECT)
          .eq('tutor_id', input.tutorId)
          .eq('student_id', input.studentId)
          .eq('subject_scope', input.subject.scope)
          .eq(column, targetId)
          .maybeSingle();
        if (error)
          throw new DiscussionError(
            'list_failed',
            'Impossible de vérifier les discussions existantes.',
          );
        if (data) return discussionFromRow(data as unknown as RawDiscussionRow);
      }
      return create.mutateAsync(input);
    },
  });
}

export interface StartCallResult {
  reservationId: string;
}

/**
 * « Démarrer un appel » depuis une discussion — tuteur seul. Le `kind` du
 * rendez-vous direct dépend de la relation réelle (lien tutor_links actif vs
 * mentorat académique), que le client ne connaît pas forcément : on tente
 * 'express' puis on retombe sur 'entretien' si le serveur le refuse (le serveur
 * reste seul juge, cf. create_direct_rendezvous, migration 0237).
 */
export function useStartCallFromDiscussion() {
  return useMutation({
    mutationFn: async (discussion: DiscussionRow): Promise<StartCallResult> => {
      const startsAt = new Date().toISOString();
      let reservationId: string | null = null;
      for (const kind of ['express', 'entretien'] as const) {
        const { data, error } = await db
          .rpc('create_direct_rendezvous', {
            p_kind: kind,
            p_student_id: discussion.studentId,
            p_starts_at: startsAt,
            p_duration_minutes: 30,
          })
          .single();
        if (!error && data) {
          reservationId = (data as { reservation_id: string }).reservation_id;
          break;
        }
      }
      if (!reservationId) {
        throw new DiscussionError('call_failed', 'Impossible de démarrer un appel avec cet élève.');
      }

      const { data: roomRows, error: roomError } = await db.rpc('get_or_create_visio_room', {
        p_reservation_id: reservationId,
      });
      const roomId = (roomRows as Array<{ room_id: string }> | null)?.[0]?.room_id;
      if (roomError || !roomId) {
        throw new DiscussionError('call_failed', "La salle de l'appel n'a pas pu être créée.");
      }

      const { error: linkError } = await db.rpc('link_visio_room_to_discussion', {
        p_room_id: roomId,
        p_discussion_id: discussion.id,
      });
      if (linkError) {
        // Best-effort : l'appel EXISTE déjà — un échec de rattachement ne doit
        // pas empêcher de le rejoindre (le fil ne verra juste pas son chat live).
        console.error('[discussions] rattachement de la salle échoué', linkError);
      }

      return { reservationId };
    },
  });
}
